import React, { useState } from "react";
import {
  Eye,
  Clock,
  Check,
  X,
  MoreVertical,
  Hash,
  FileText,
} from "lucide-react";
import { BanRequest } from "../../api/services/ban/type";
import {
  useApproveBanRequest,
  useRejectBanRequest,
} from "../../hooks/useBanRequests";
import { UserName } from "../customers/customerDetail/UserName";
import { CustomerName } from "./CustomerName";

interface BanRequestsTableProps {
  banRequests: BanRequest[];
  currentPage: number;
  itemsPerPage: number;
}

export const BanRequestsTable: React.FC<BanRequestsTableProps> = ({
  banRequests,
  currentPage,
  itemsPerPage,
}) => {
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const approveMutation = useApproveBanRequest();
  const rejectMutation = useRejectBanRequest();

  const getBanTypeInfo = (banType: string) => {
    switch (banType) {
      case "under_monitoring":
      case "under_monitor":
        return {
          label: "Under Monitoring",
          color: "text-yellow-400",
          bgColor: "bg-yellow-400/10",
        };
      case "temporary_ban":
        return {
          label: "Temporary Ban",
          color: "text-orange-400",
          bgColor: "bg-orange-400/10",
        };
      case "permanently_banned":
        return {
          label: "Permanent Ban",
          color: "text-red-400",
          bgColor: "bg-red-400/10",
        };
      default:
        return {
          label: banType,
          color: "text-slate-400",
          bgColor: "bg-slate-400/10",
        };
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "pending":
        return {
          label: "Pending",
          color: "text-orange-400",
          bgColor: "bg-orange-400/10",
          icon: <Clock className="w-3 h-3" />,
        };
      case "approved":
        return {
          label: "Approved",
          color: "text-green-400",
          bgColor: "bg-green-400/10",
          icon: <Check className="w-3 h-3" />,
        };
      case "rejected":
        return {
          label: "Rejected",
          color: "text-red-400",
          bgColor: "bg-red-400/10",
          icon: <X className="w-3 h-3" />,
        };
      default:
        return {
          label: status,
          color: "text-slate-400",
          bgColor: "bg-slate-400/10",
          icon: <FileText className="w-3 h-3" />,
        };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleApprove = async (banRequestId: string) => {
    try {
      await approveMutation.mutateAsync(banRequestId);
      setActionMenuOpen(null);
    } catch (error) {
      console.error("Failed to approve ban request:", error);
    }
  };

  const handleReject = async (banRequestId: string) => {
    if (!rejectionReason.trim()) return;

    try {
      await rejectMutation.mutateAsync({
        banRequestId,
        data: { rejectionReason: rejectionReason.trim() },
      });
      setRejectModalOpen(null);
      setRejectionReason("");
      setActionMenuOpen(null);
    } catch (error) {
      console.error("Failed to reject ban request:", error);
    }
  };

  if (banRequests.length === 0) {
    return (
      <div className="p-8 text-center text-slate-400">
        <Clock className="w-12 h-12 mx-auto mb-4 text-slate-500" />
        <h3 className="text-lg font-medium text-slate-300 mb-2">
          No Pending Requests
        </h3>
        <p>There are no pending ban requests at the moment.</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700">
            <tr>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                S.N.
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Ban Type
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Ban End Date
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Requested By
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Requested At
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Remarks
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {banRequests.map((request, index) => {
              const banTypeInfo = getBanTypeInfo(request.banType);
              const statusInfo = getStatusInfo(request.status);
              const serialNumber = (currentPage - 1) * itemsPerPage + index + 1;

              return (
                <tr
                  key={request.id}
                  className="hover:bg-slate-700 transition-colors"
                >
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                    {serialNumber}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="ml-0">
                        <div className="text-sm font-medium text-white flex items-center gap-2 mb-1">
                          <Hash className="w-3 h-3 text-slate-400" />
                          <span className="text-xs text-slate-400">
                            {request.customerId.slice(-8)}
                          </span>
                        </div>
                        <CustomerName
                          customerId={request.customerId}
                          showEmail={true}
                          showPlayerId={true}
                          fallback="Unknown Customer"
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full ${banTypeInfo.bgColor}`}
                    >
                      <span
                        className={`text-sm font-medium ${banTypeInfo.color}`}
                      >
                        {banTypeInfo.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                    {request.banEndDate
                      ? formatDate(request.banEndDate)
                      : "N/A"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <UserName
                      userId={request.requestedBy}
                      fallback="Unknown Staff"
                    />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                    {formatDate(request.requestedAt || request.createdAt)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full ${statusInfo.bgColor}`}
                    >
                      {statusInfo.icon}
                      <span
                        className={`text-sm font-medium ${statusInfo.color}`}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 max-w-xs">
                    <div
                      className="text-sm text-slate-300 truncate"
                      title={request.remarks}
                    >
                      {request.remarks}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                    <div className="relative">
                      <button
                        onClick={() =>
                          setActionMenuOpen(
                            actionMenuOpen === request.id ? null : request.id,
                          )
                        }
                        className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-600 transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {actionMenuOpen === request.id && (
                        <div className="absolute right-0 top-10 mt-1 w-48 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-10">
                          <div className="py-1">
                            {request.status === "pending" && (
                              <>
                                <button
                                  onClick={() => handleApprove(request.id)}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-white hover:bg-slate-600"
                                >
                                  <Check className="w-4 h-4 text-green-400" />
                                  Approve
                                </button>
                                <button
                                  onClick={() => setRejectModalOpen(request.id)}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-white hover:bg-slate-600"
                                >
                                  <X className="w-4 h-4 text-red-400" />
                                  Reject
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => {
                                /* Handle view details */
                              }}
                              className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-white hover:bg-slate-600"
                            >
                              <Eye className="w-4 h-4" />
                              View Details
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Rejection Modal */}
      {rejectModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setRejectModalOpen(null)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between p-6 border-b border-slate-700">
                <h3 className="text-lg font-semibold text-white">
                  Reject Ban Request
                </h3>
                <button
                  onClick={() => setRejectModalOpen(null)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Rejection Reason *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a reason for rejecting this request..."
                    required
                    rows={4}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setRejectModalOpen(null)}
                    className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleReject(rejectModalOpen)}
                    disabled={!rejectionReason.trim()}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reject Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Close dropdown when clicking outside */}
      {actionMenuOpen && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setActionMenuOpen(null)}
        />
      )}
    </>
  );
};
